import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAuthModal } from "../context/AuthModalContext";
import {
  getProfileAvatarUrl,
  getProfileDisplayLabel,
  getProfileInitial,
  getProfileUserId,
} from "../utils/userDisplay";

const AVATAR_SIZE_CLASS = {
  xs: "h-4 w-4 text-[8px]",
  sm: "h-6 w-6 text-caption-1",
  md: "h-8 w-8 text-footnote",
  lg: "h-10 w-10 text-subheadline",
  xl: "h-14 w-14 text-base",
};

/**
 * Avatar + display name linking to /profile/:id.
 * Use stopPropagation when nested inside clickable cards.
 */
const UserProfileLink = ({
  user,
  avatarUrl: avatarUrlOverride,
  className = "",
  fallback = "—",
  preferUsername = false,
  stopPropagation = false,
  children,
  showAvatar = true,
  avatarSize = "sm",
  isDark = true,
  nameClassName = "",
  avatarClassName = "",
}) => {
  const userId = getProfileUserId(user);
  const label = children ?? getProfileDisplayLabel(user, { preferUsername, fallback });
  const avatarUrl = avatarUrlOverride || getProfileAvatarUrl(user);
  const initial = getProfileInitial(user, fallback);
  const sizeClass = AVATAR_SIZE_CLASS[avatarSize] || AVATAR_SIZE_CLASS.sm;
  const { isAuthenticated } = useAuth();
  const authModal = useAuthModal();

  const handleClick = (e) => {
    if (stopPropagation) e.stopPropagation();
    if (!isAuthenticated && authModal) {
      e.preventDefault();
      authModal.openAuthModal({ redirectTo: `/profile/${userId}` });
    }
  };

  const avatar = showAvatar ? (
    <span
      className={`relative inline-flex shrink-0 overflow-hidden rounded-full ring-1 ${
        isDark ? "ring-white/15" : "ring-slate-200"
      } ${sizeClass} ${avatarClassName}`}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span
          className={`flex h-full w-full items-center justify-center font-bold ${
            isDark ? "bg-accent-tinted text-accent-text" : "bg-accent-tinted text-status-success"
          }`}
        >
          {initial}
        </span>
      )}
    </span>
  ) : null;

  const name = (
    <span className={`min-w-0 truncate transition hover:text-accent-text ${nameClassName}`}>{label}</span>
  );

  const content = (
    <>
      {avatar}
      {name}
    </>
  );

  const layoutClass = `inline-flex min-w-0 max-w-full items-center gap-1.5 ${className}`;

  if (!userId) {
    return <span className={layoutClass}>{content}</span>;
  }

  return (
    <Link to={`/profile/${userId}`} onClick={handleClick} className={layoutClass}>
      {content}
    </Link>
  );
};

export default UserProfileLink;
